import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  FileText,
  Eye,
  Edit2,
  Printer,
  User,
  MapPin,
  Phone,
  Calendar,
  Wrench,
  ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample job card data for preview - matches actual job card structure
const SAMPLE_DATA = {
  job_card_id: "jc_sample_001",
  booking_id: "SAMPLE-001",
  customer_name: "John Smith",
  customer_phone: "+27 82 123 4567",
  customer_address: "123 Main Street, Cape Town, 8001",
  contact_person: "John Smith",
  service_date: new Date().toISOString().split("T")[0],
  service_name: "4-Tap Draught System Service",
  installation_name: "Main Bar Counter",
  technician_name: "Mike Wilson",
  status: "pending",
  sanitation_checklist: {
    taps_cleaned: true,
    coolers_cleaned: true,
    column_lights_working: false,
    columns_drip_trays_cleaned: true,
    python_cleaned: false,
    regulators_set: false,
    beer_lines_sanitised: false,
    couplers_sanitised: false,
    record_board_stickers: false,
  },
  tap_verifications: [
    { tap_number: 1, verified: true, brand: "Castle Lager" },
    { tap_number: 2, verified: true, brand: "Black Label" },
    { tap_number: 3, verified: false, brand: "" },
    { tap_number: 4, verified: false, brand: "" },
  ],
  asset_verifications: [
    { asset_id: "COOL-001", serial_number: "SN-2024-0001", quantity: 1, notes: "Under counter cooler" },
    { asset_id: "REG-001", serial_number: "SN-2024-0002", quantity: 2, notes: "CO2 Regulators" },
  ],
  worker_notes: "Customer requested additional python cleaning due to buildup noticed.",
};

const SANITATION_ITEMS = [
  { key: "taps_cleaned", label: "Taps Cleaned?" },
  { key: "coolers_cleaned", label: "Cooler/s Cleaned?" },
  { key: "column_lights_working", label: "Column Lights Working?" },
  { key: "columns_drip_trays_cleaned", label: "Column/s & Drip Trays Cleaned?" },
  { key: "python_cleaned", label: "Python Cleaned?" },
  { key: "regulators_set", label: "Regulator/s Set?" },
  { key: "beer_lines_sanitised", label: "Beer Line/s Sanitised?" },
  { key: "couplers_sanitised", label: "Coupler/s Sanitised?" },
  { key: "record_board_stickers", label: "Record Board Sticker/s?" },
];

const JobCardTemplate = () => {
  const [viewMode, setViewMode] = useState("preview");
  const [companyInfo, setCompanyInfo] = useState({
    name: "TaskForge Manufacturing",
    address: "123 Industrial Way, Cape Town, 8001",
    phone: "+27 21 555 0100",
    email: "service@taskforge.co.za",
    logo_url: "",
  });
  const [saving, setSaving] = useState(false);

  // Load company info from settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`, { withCredentials: true });
        const settings = response.data;
        if (settings?.company_name || settings?.company_logo) {
          setCompanyInfo(prev => ({
            ...prev,
            name: settings.company_name || prev.name,
            logo_url: settings.company_logo || prev.logo_url,
            address: settings.company_address || prev.address,
            phone: settings.company_phone || prev.phone,
            email: settings.company_email || prev.email,
          }));
        }
        // Also check localStorage for saved template settings
        const saved = localStorage.getItem("jobCardCompanyInfo");
        if (saved) {
          const parsed = JSON.parse(saved);
          setCompanyInfo(prev => ({ ...prev, ...parsed }));
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      localStorage.setItem("jobCardCompanyInfo", JSON.stringify(companyInfo));
      toast.success("Template settings saved");
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setCompanyInfo({
      name: "TaskForge Manufacturing",
      address: "123 Industrial Way, Cape Town, 8001",
      phone: "+27 21 555 0100",
      email: "service@taskforge.co.za",
      logo_url: "",
    });
    localStorage.removeItem("jobCardCompanyInfo");
    toast.info("Template reset to defaults");
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="p-6 md:p-8" data-testid="job-card-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Job Card Template
          </h1>
          <p className="text-slate-500 mt-1">
            Preview the actual job card structure used for service bookings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "preview" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("preview")}
            className="rounded-sm"
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button
            variant={viewMode === "edit" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("edit")}
            className="rounded-sm"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Edit Info
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            className="rounded-sm print:hidden"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {viewMode === "edit" ? (
        /* Edit Mode - Company Info Form */
        <Card className="rounded-sm border-slate-200 max-w-2xl">
          <CardHeader>
            <CardTitle className="text-lg">Company Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={companyInfo.name}
                onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={companyInfo.address}
                onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={companyInfo.phone}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={companyInfo.email}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Logo URL</Label>
              <Input
                value={companyInfo.logo_url}
                onChange={(e) => setCompanyInfo({ ...companyInfo, logo_url: e.target.value })}
                className="rounded-sm"
                placeholder="https://example.com/logo.png"
              />
              <p className="text-xs text-slate-500">
                Upload your logo in Settings, or enter a direct URL here
              </p>
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSave} disabled={saving} className="rounded-sm">
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              <Button variant="outline" onClick={handleReset} className="rounded-sm">
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Preview Mode - Actual Job Card Structure */
        <div className="max-w-4xl mx-auto">
          <Card className="rounded-sm border-slate-200 print:shadow-none print:border-0">
            <CardContent className="p-8">
              {/* Header with Company Info */}
              <div className="flex items-start justify-between border-b pb-6 mb-6">
                <div>
                  {companyInfo.logo_url ? (
                    <img src={companyInfo.logo_url} alt="Logo" className="h-12 mb-2 object-contain" />
                  ) : (
                    <div className="flex items-center gap-2 mb-2">
                      <ClipboardCheck className="h-8 w-8 text-indigo-600" />
                      <span className="text-2xl font-bold text-slate-900">{companyInfo.name}</span>
                    </div>
                  )}
                  {companyInfo.logo_url && (
                    <p className="text-lg font-semibold text-slate-900">{companyInfo.name}</p>
                  )}
                  <p className="text-sm text-slate-500">{companyInfo.address}</p>
                  <p className="text-sm text-slate-500">{companyInfo.phone} | {companyInfo.email}</p>
                </div>
                <div className="text-right">
                  <h1 className="text-2xl font-bold text-indigo-600">JOB CARD</h1>
                  <p className="text-sm text-slate-500 mt-1">Ref: {SAMPLE_DATA.booking_id}</p>
                  <p className="text-sm text-slate-500">Date: {SAMPLE_DATA.service_date}</p>
                  <Badge className="mt-2 rounded-sm">{SAMPLE_DATA.status}</Badge>
                </div>
              </div>

              {/* Customer & Service Info */}
              <div className="grid grid-cols-2 gap-8 mb-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 border-b pb-1 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Customer Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex">
                      <span className="w-24 shrink-0 text-slate-500">Name:</span>
                      <span className="flex-1 min-w-0 font-medium break-words">{SAMPLE_DATA.customer_name}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 shrink-0 text-slate-500">Phone:</span>
                      <span className="flex-1 min-w-0 break-words">{SAMPLE_DATA.customer_phone}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 shrink-0 text-slate-500">Address:</span>
                      <span className="flex-1 min-w-0 break-words">{SAMPLE_DATA.customer_address}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 shrink-0 text-slate-500">Contact:</span>
                      <span className="flex-1 min-w-0 break-words">{SAMPLE_DATA.contact_person}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 border-b pb-1 flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    Service Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex">
                      <span className="w-24 shrink-0 text-slate-500">Service:</span>
                      <span className="flex-1 min-w-0 font-medium break-words">{SAMPLE_DATA.service_name}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 shrink-0 text-slate-500">Date:</span>
                      <span className="flex-1 min-w-0 break-words">{SAMPLE_DATA.service_date}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 shrink-0 text-slate-500">Installation:</span>
                      <span className="flex-1 min-w-0 break-words">{SAMPLE_DATA.installation_name}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 shrink-0 text-slate-500">Technician:</span>
                      <span className="flex-1 min-w-0 break-words">{SAMPLE_DATA.technician_name}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sanitation Check List */}
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-1">Sanitation Check List</h3>
                <div className="grid grid-cols-3 gap-3">
                  {SANITATION_ITEMS.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2 p-2 border rounded-sm bg-slate-50">
                      <Checkbox checked={SAMPLE_DATA.sanitation_checklist[key]} disabled />
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tap Verification */}
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-1">Tap Verification</h3>
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left p-2 border-r w-20">Tap #</th>
                      <th className="text-center p-2 border-r w-24">Verified</th>
                      <th className="text-left p-2">Brand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SAMPLE_DATA.tap_verifications.map((tap) => (
                      <tr key={`tap-${tap.tap_number}`} className="border-t">
                        <td className="p-2 border-r font-medium">{tap.tap_number}</td>
                        <td className="p-2 border-r text-center">
                          <Checkbox checked={tap.verified} disabled />
                        </td>
                        <td className="p-2">{tap.brand || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-slate-400 mt-1 italic">
                  * Technicians can add/remove tap rows as needed
                </p>
              </div>

              {/* Asset Verification */}
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-1">Asset Verification</h3>
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left p-2 border-r">Asset ID</th>
                      <th className="text-left p-2 border-r">Serial Number</th>
                      <th className="text-left p-2 border-r w-20">Qty</th>
                      <th className="text-left p-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SAMPLE_DATA.asset_verifications.map((asset) => (
                      <tr key={`asset-${asset.asset_id}-${asset.serial_number}`} className="border-t">
                        <td className="p-2 border-r">{asset.asset_id}</td>
                        <td className="p-2 border-r">{asset.serial_number}</td>
                        <td className="p-2 border-r">{asset.quantity}</td>
                        <td className="p-2">{asset.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-slate-400 mt-1 italic">
                  * Assets can be loaded from customer's installation systems
                </p>
              </div>

              {/* Technician Notes */}
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-1">Technician Notes</h3>
                <div className="border rounded-sm p-3 min-h-[60px] bg-slate-50 text-sm">
                  {SAMPLE_DATA.worker_notes || <span className="text-slate-400">[Notes will appear here]</span>}
                </div>
              </div>

              {/* Signatures */}
              <div className="grid grid-cols-2 gap-8 pt-4 border-t">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Technician Signature</h3>
                  <div className="border border-dashed border-slate-300 rounded h-24 flex items-center justify-center text-sm text-slate-400 bg-slate-50">
                    [Digital Signature with GPS]
                  </div>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-slate-500">Name: _______________</span>
                    <span className="text-slate-500">Date: _______________</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Customer Signature</h3>
                  <div className="border border-dashed border-slate-300 rounded h-24 flex items-center justify-center text-sm text-slate-400 bg-slate-50">
                    [Digital Signature with GPS]
                  </div>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-slate-500">Name: _______________</span>
                    <span className="text-slate-500">Date: _______________</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-4 border-t text-center text-xs text-slate-400">
                <p>This job card is a legal document. Please retain for your records.</p>
                <p>Generated by {companyInfo.name} • {new Date().toLocaleDateString()}</p>
              </div>
            </CardContent>
          </Card>

          {/* Print Styles Info */}
          <p className="text-sm text-slate-500 text-center mt-4 print:hidden">
            Use the Print button to generate a PDF or print this template
          </p>
        </div>
      )}
    </div>
  );
};

export default JobCardTemplate;
